import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from '../../../../../../../environments/environment';
import { AppState } from '../../../../../../core/reducers';
import { currentUser } from '../../../../../../core/auth';
import { ActivityRecord, ActivityRecordParams } from '../models';
import { RelatedView } from '../enums';

@Injectable({
  providedIn: 'root',
})
export class ContextualHistoryService {
  private companyId: number;
  private baseUrl = environment.CORE_BASE;
  // please don't change the order of array items.
  private relatedPaths = [
    '/lists/contacts/edit',
    '/lists/contacts/view',
    '/lists/patients/edit',
    '/lists/patients/view',
    '/filtered-view/dashboard',
    '/med-review/board',
  ];
  showIconSub = new BehaviorSubject(false);
  contactIdSub = new BehaviorSubject(0);
  relatedViewSub = new BehaviorSubject<RelatedView | ''>('');
  relatedPatientsIdSub = new BehaviorSubject<number[]>(null);

  constructor(private http: HttpClient, private store: Store<AppState>, private router: Router) {
    this.store.select(currentUser).subscribe((res) => (this.companyId = res.companyID));
    this.getLocalStorageDate();
    this.clearLocalStorageData();

    window.onbeforeunload = () => {
      if (this.relatedPaths.some((path) => this.router.url.includes(path)))
        this.setLocalStorageData();
    };

    this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe((event: NavigationStart) => {
        this.setFieldsData(event.url);
      });
  }

  setFieldsData(url: string, contactId?: number, relatedPatientsId?: number[]) {
    if (!this.relatedPaths.some((path) => url.includes(path))) return this.reset();

    this.showIconSub.next(this.relatedPaths.some((path) => url.includes(path)));

    if (url.includes(this.relatedPaths[0]) || url.includes(this.relatedPaths[1])) {
      this.contactIdSub.next(parseInt(url.match(/\d+/)[0], 10));
      this.relatedPatientsIdSub.next(null);
      this.relatedViewSub.next(RelatedView.CONTACT);
      return;
    }

    if (url.includes(this.relatedPaths[2]) || url.includes(this.relatedPaths[3])) {
      this.contactIdSub.next(0);
      this.relatedPatientsIdSub.next([parseInt(url.match(/\d+/)[0], 10)]);
      this.relatedViewSub.next(RelatedView.PATIENT);
      return;
    }

    if (url.includes(this.relatedPaths[4]) && contactId) {
      this.contactIdSub.next(contactId);
      this.relatedPatientsIdSub.next(relatedPatientsId || null);
      this.relatedViewSub.next(RelatedView.FILTERED);
    }

    if (url.includes(this.relatedPaths[5]) && relatedPatientsId?.length > 0) {
      this.contactIdSub.next(0);
      this.relatedPatientsIdSub.next(relatedPatientsId || null);
      this.relatedViewSub.next(RelatedView.PATIENT);
    }
  }

  setLocalStorageData() {
    localStorage.setItem('showIconCH', this.showIconSub.value ? 'true' : 'false');
    localStorage.setItem('contactIdCH', this.contactIdSub.value + '');
    localStorage.setItem('relatedViewCH', this.relatedViewSub.value);
    localStorage.setItem('relatedPatientsIdCH', JSON.stringify(this.relatedPatientsIdSub.value));
  }

  getLocalStorageDate() {
    this.showIconSub.next(localStorage.getItem('showIconCH') === 'true');
    this.contactIdSub.next(
      localStorage.getItem('contactIdCH') ? parseInt(localStorage.getItem('contactIdCH'), 10) : 0,
    );
    this.relatedViewSub.next(
      localStorage.getItem('relatedViewCH')
        ? RelatedView[localStorage.getItem('relatedViewCH')]
        : '',
    );
    this.relatedPatientsIdSub.next(
      localStorage.getItem('relatedPatientsIdCH')
        ? JSON.parse(localStorage.getItem('relatedPatientsIdCH'))
        : null,
    );
  }

  clearLocalStorageData() {
    localStorage.removeItem('showIconCH');
    localStorage.removeItem('contactIdCH');
    localStorage.removeItem('relatedViewCH');
    localStorage.removeItem('relatedPatientsIdCH');
  }

  reset() {
    this.clearLocalStorageData();
    this.getLocalStorageDate();
  }

  getActivityRecords(
    query: Partial<ActivityRecordParams>,
    body: number[] = null,
  ): Observable<{ data: ActivityRecord[] }> {
    const { contact_id, month, team_id, is_backstage, search_term } = query;
    let fromString = `contact_id=${contact_id || ''}&month=${month || 0}&team_id=${
      team_id || ''
    }&search_term=${search_term || ''}`;

    if (is_backstage) fromString += `&is_backstage=${is_backstage}`;

    const params = new HttpParams({ fromString });

    return this.http.post<{ data: ActivityRecord[] }>(
      `${this.baseUrl}/companies/${this.companyId}/search/contextual-history`,
      body,
      { params },
    );
  }
}
